import React, { useState } from "react";
import axios from "axios";
import { Mail, Phone, MapPin, Send, Check } from "lucide-react";
import { toast } from "sonner";
import { FAQS } from "../../lib/data";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  "Custom Website Development",
  "Mobile & Web App Development",
  "Marketing & Lead Generation",
  "Branding & Visual Identity",
  "SEO & Local Growth",
  "Not sure — let's chat",
];

const BUDGET_OPTIONS = [
  "£200 – £500",
  "£500 – £1k",
  "£1k – £2.5k",
  "£2.5k – £5k",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    business: "",
    service: SERVICE_OPTIONS[0],
    budget: BUDGET_OPTIONS[0],
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);

  const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setDone(true);
      toast.success("Thank you. We'll be in touch within one business day.");
      setForm({
        name: "",
        email: "",
        phone: "",
        business: "",
        service: SERVICE_OPTIONS[0],
        budget: BUDGET_OPTIONS[0],
        message: "",
      });
    } catch (err) {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 md:py-32 bg-[#050505]"
    >
      <div className="mx-auto max-w-[1500px] px-6 md:px-12 lg:px-24">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Left intro */}
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-gold"></span>
              <span className="text-[11px] uppercase tracking-[0.32em] text-white/60">
                Begin the conversation
              </span>
            </div>
            <h2 className="font-display text-white text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight">
              Let's build something
              <br />
              <em className="gold-text-gradient not-italic">exceptional</em>.
            </h2>
            <p className="mt-8 text-white/60 leading-relaxed">
              Tell us about your business and your ambitions. A senior strategist will
              respond within one working day with a tailored, no-obligation plan.
            </p>

            <div className="mt-12 space-y-6">
              <a
                href="mailto:info@acnira.co.uk"
                data-testid="contact-email"
                className="group flex items-center gap-4 text-white/80 hover:text-gold transition-colors"
              >
                <span className="h-12 w-12 flex items-center justify-center border border-white/15 group-hover:border-gold/50 transition-colors">
                  <Mail size={18} />
                </span>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">
                    Email
                  </div>
                  <div className="text-base">info@acnira.co.uk</div>
                </div>
              </a>
              <a
                href="https://wa.me/447767954568?text=Hello%20Acnira%2C%20I%27d%20like%20to%20chat%20about%20a%20project."
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-phone"
                className="group flex items-center gap-4 text-white/80 hover:text-gold transition-colors"
              >
                <span className="h-12 w-12 flex items-center justify-center border border-white/15 group-hover:border-gold/50 transition-colors">
                  <Phone size={18} />
                </span>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">
                    WhatsApp · Call
                  </div>
                  <div className="text-base">+44 07767 954568</div>
                </div>
              </a>
              <div className="flex items-center gap-4 text-white/80">
                <span className="h-12 w-12 flex items-center justify-center border border-white/15">
                  <MapPin size={18} />
                </span>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">
                    Studio
                  </div>
                  <div className="text-base">The Works, 33 Withy Grove, Manchester M4 2BJ</div>
                </div>
              </div>
            </div>

            {/* FAQs */}
            <div className="mt-14" data-testid="faq-list">
              <div className="text-[11px] uppercase tracking-[0.3em] text-white/50 mb-5">
                Frequently asked
              </div>
              <div className="border-t border-white/10">
                {FAQS.map((f, i) => (
                  <div key={i} className="border-b border-white/10">
                    <button
                      data-testid={`faq-toggle-${i}`}
                      onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                      className="w-full flex items-center justify-between py-5 text-left"
                    >
                      <span className="font-display text-white text-lg pr-6">{f.q}</span>
                      <span className="text-gold text-xl">
                        {openFaq === i ? "–" : "+"}
                      </span>
                    </button>
                    {openFaq === i && (
                      <p className="pb-5 text-white/55 text-sm leading-relaxed">
                        {f.a}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right form */}
          <div className="lg:col-span-7">
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="glass-strong p-8 md:p-12 relative"
            >
              <div className="absolute top-0 left-0 h-px w-full bg-gradient-to-r from-transparent via-gold/60 to-transparent"></div>

              {done ? (
                <div data-testid="contact-success" className="py-16 text-center">
                  <div className="mx-auto h-16 w-16 rounded-full gold-bg-gradient flex items-center justify-center text-black mb-6">
                    <Check size={28} strokeWidth={2.5} />
                  </div>
                  <h3 className="font-display text-3xl text-white mb-3">
                    Thank you.
                  </h3>
                  <p className="text-white/60 max-w-md mx-auto">
                    Your enquiry has been received. A senior strategist will be in
                    touch within one working day.
                  </p>
                  <button
                    type="button"
                    onClick={() => setDone(false)}
                    data-testid="send-another"
                    className="mt-8 text-sm text-gold underline underline-offset-4"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <Field
                      label="Full name"
                      required
                      testid="input-name"
                      value={form.name}
                      onChange={change("name")}
                    />
                    <Field
                      label="Email"
                      type="email"
                      required
                      testid="input-email"
                      value={form.email}
                      onChange={change("email")}
                    />
                    <Field
                      label="Phone"
                      testid="input-phone"
                      value={form.phone}
                      onChange={change("phone")}
                    />
                    <Field
                      label="Business name"
                      testid="input-business"
                      value={form.business}
                      onChange={change("business")}
                    />
                    <SelectField
                      label="Service"
                      options={SERVICE_OPTIONS}
                      testid="select-service"
                      value={form.service}
                      onChange={change("service")}
                    />
                    <SelectField
                      label="Budget"
                      options={BUDGET_OPTIONS}
                      testid="select-budget"
                      value={form.budget}
                      onChange={change("budget")}
                    />
                  </div>

                  <div className="mt-5">
                    <label className="block text-[10px] uppercase tracking-[0.3em] text-white/50 mb-2">
                      Tell us about your project *
                    </label>
                    <textarea
                      required
                      data-testid="input-message"
                      value={form.message}
                      onChange={change("message")}
                      rows={5}
                      className="w-full bg-black/50 border border-white/15 px-4 py-3 text-white placeholder-white/30 focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold/40 transition-all resize-none"
                      placeholder="A few sentences about your business, goals and timelines."
                    />
                  </div>

                  <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
                    <p className="text-xs text-white/40 max-w-xs">
                      By submitting, you agree to be contacted by Acnira about your
                      enquiry. We never share your data.
                    </p>
                    <button
                      type="submit"
                      data-testid="contact-submit"
                      disabled={submitting}
                      className="shine inline-flex items-center justify-center gap-2 px-8 py-4 text-sm font-semibold gold-bg-gradient text-black hover:opacity-90 transition-opacity disabled:opacity-60 disabled:cursor-not-allowed w-full sm:w-auto"
                    >
                      {submitting ? "Sending…" : "Submit Enquiry"}
                      <Send size={16} />
                    </button>
                  </div>
                </>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, required, type = "text", value, onChange, testid }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.3em] text-white/50 mb-2">
        {label} {required && "*"}
      </label>
      <input
        required={required}
        type={type}
        data-testid={testid}
        value={value}
        onChange={onChange}
        className="w-full bg-black/50 border border-white/15 px-4 py-3 text-white placeholder-white/30 focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold/40 transition-all"
      />
    </div>
  );
}

function SelectField({ label, options, value, onChange, testid }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.3em] text-white/50 mb-2">
        {label}
      </label>
      <select
        data-testid={testid}
        value={value}
        onChange={onChange}
        className="w-full bg-black/50 border border-white/15 px-4 py-3 text-white focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold/40 transition-all appearance-none"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23D4AF37' stroke-width='2'><polyline points='6 9 12 15 18 9'/></svg>\")",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "right 1rem center",
          paddingRight: "2.5rem",
        }}
      >
        {options.map((o) => (
          <option key={o} value={o} className="bg-[#0f0f0f] text-white">
            {o}
          </option>
        ))}
      </select>
    </div>
  );
}
